import { useState } from "react";
import { LANG_NAMES, LANGS } from "../lib/scenario";
import type { Key, Lang, ScenarioContent } from "../lib/scenario";
import Illustration from "./Illustration";

// The scenario as it will appear on a phone (screens 4 to 7), in the phone's own words.
// Tapping through it works like the app, except the options stay in A, B, C order here;
// the phone shuffles them.

const UI: Record<Lang, Record<string, string>> = {
  en: {
    listen: "Listen", choose: "What will you do?", chooseOne: "Choose one", seeWhy: "See why",
    best: "Good answer", risky: "Risky", wrong: "Not right", good: "Good answer!", tryNext: "Try again next time",
    wentWell: "What went well", remember: "Remember", say: "You can say", next: "Next practice", again: "Try again",
    minutes: "About {n} min",
  },
  mr: {
    listen: "ऐका", choose: "तुम्ही काय कराल?", chooseOne: "एक निवडा", seeWhy: "का ते बघा",
    best: "बरोबर उत्तर", risky: "रिस्की", wrong: "बरोबर नाही", good: "छान उत्तर!", tryNext: "पुढच्या वेळी परत ट्राय करा",
    wentWell: "काय चांगलं झालं", remember: "लक्षात ठेवा", say: "तुम्ही असं बोलू शकता", next: "पुढची प्रॅक्टिस",
    again: "परत ट्राय करा", minutes: "साधारण {n} मिनिटं",
  },
  hi: {
    listen: "सुनें", choose: "आप क्या करेंगे?", chooseOne: "एक चुनें", seeWhy: "क्यों, ये देखें",
    best: "सही जवाब", risky: "रिस्की", wrong: "सही नहीं", good: "अच्छा जवाब!", tryNext: "अगली बार फिर ट्राय करें",
    wentWell: "क्या अच्छा रहा", remember: "याद रखें", say: "आप ऐसे बोल सकते हैं", next: "अगली प्रैक्टिस",
    again: "फिर से ट्राय करें", minutes: "करीब {n} मिनट",
  },
};

type Step = "situation" | "choose" | "outcome" | "feedback";

function Text({ value, missing }: { value: string | undefined; missing: string }) {
  return value?.trim() ? <>{value}</> : <span className="missing-text">{missing}</span>;
}

export default function PhonePreview({ content }: { content: ScenarioContent }) {
  const [lang, setLang] = useState<Lang>("mr");
  const [step, setStep] = useState<Step>("situation");
  const [chosen, setChosen] = useState<Key>("A");
  const ui = UI[lang];
  const scene = content.text[lang];
  const option = content.options.find((o) => o.key === chosen)!;
  const best = content.options.find((o) => o.quality === "best");
  const t = option.text[lang];
  const b = best?.text[lang];
  const listen = (path: string | undefined) => (path ? <span className="listen">🔊 {ui.listen}</span> : null);

  return (
    <div className="stack">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="segmented" role="group" aria-label="Preview language">
          {LANGS.map((l) => (
            <button key={l} className={lang === l ? "on" : ""} onClick={() => setLang(l)}>
              {LANG_NAMES[l]}
            </button>
          ))}
        </div>
        {step !== "situation" && (
          <button className="link-btn" onClick={() => setStep("situation")}>
            Start again
          </button>
        )}
      </div>
      <div className="phone" aria-label="Phone preview">
        <div className="screen">
          {step === "situation" && (
            <>
              <div className="title">
                <Text value={scene.title} missing="Title missing" />
              </div>
              <span className="small muted">{ui.minutes.replace("{n}", String(content.estMinutes || "?"))}</span>
              <Illustration name={scene.illustrationKey} />
              <div className="body">
                <Text value={scene.situation} missing="Situation missing" />
              </div>
              {listen(scene.situationAudio)}
              <button className="btn main" onClick={() => setStep("choose")}>
                {ui.choose}
              </button>
            </>
          )}
          {step === "choose" && (
            <>
              <div className="title">{ui.choose}</div>
              <span className="muted">{ui.chooseOne}</span>
              {content.options.map((o) => (
                <button
                  key={o.key}
                  className="option"
                  onClick={() => {
                    setChosen(o.key);
                    setStep("outcome");
                  }}
                >
                  <span className="letter">{o.key}</span>
                  <span>
                    <Text value={o.text[lang].text} missing="Response missing" />
                  </span>
                </button>
              ))}
            </>
          )}
          {step === "outcome" && (
            <>
              <div className={`banner ${option.quality}`}>{ui[option.quality]}</div>
              <Illustration name={scene.illustrationKey} />
              <div className="body">
                <Text value={t.outcome} missing="Outcome missing" />
              </div>
              {listen(t.outcomeAudio)}
              <button className="btn main" onClick={() => setStep("feedback")}>
                {ui.seeWhy}
              </button>
            </>
          )}
          {step === "feedback" && (
            <>
              <div className="title">{option.quality === "best" ? `✓ ${ui.good}` : ui.tryNext}</div>
              {!!t.feedbackWentWell?.filter((l) => l.trim()).length && (
                <div className="card">
                  <strong>{ui.wentWell}</strong>
                  {t.feedbackWentWell.filter((l) => l.trim()).map((l, i) => (
                    <p key={i}>✓ {l}</p>
                  ))}
                </div>
              )}
              <div className="remember">
                <strong>{ui.remember}</strong>
                <p>
                  <Text value={b?.keyRule} missing="Rule to remember missing" />
                </p>
              </div>
              <div className="say">
                <strong>{ui.say}</strong>
                <p>
                  “<Text value={b?.sampleLine} missing="Line missing" />”
                </p>
                {b?.sampleLineAudio && <p className="small">🔊 {ui.listen}</p>}
              </div>
              <button className="btn main" onClick={() => setStep("situation")}>
                {ui.next}
              </button>
              <button className="btn outline" onClick={() => setStep("choose")}>
                {ui.again}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
